import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';

import { environment } from '../../../environments/environment';
import {
  BankAccount,
  CipcRegistrationDatePayload,
  CreateBankAccountPayload,
  EntityVerificationPayload,
  EntityVerificationState,
} from './entity-verification.types';

/** Ops-only: a business's entity-level verification record and its bank accounts. */
@Injectable({ providedIn: 'root' })
export class EntityVerificationService {
  private readonly http = inject(HttpClient);

  // -------------------------------------------------------------------------
  // BRE-200 §2.4 entity-level verifications (Ops-only, FLOW-160)
  // -------------------------------------------------------------------------

  /** Read the canonical verification record for a business. Ops-only (N4). */
  opsGetVerification(shortId: string): Observable<EntityVerificationState> {
    return this.http.get<EntityVerificationState>(
      `${environment.apiUrl}/api/entities/ops/${shortId}/verification/`,
    );
  }

  /**
   * Record one or more verifications. Send only what was actually checked —
   * the payload is partial by design, and an omitted field is left untouched
   * rather than cleared.
   */
  opsRecordVerification(
    shortId: string,
    payload: EntityVerificationPayload,
  ): Observable<EntityVerificationState> {
    return this.http.post<EntityVerificationState>(
      `${environment.apiUrl}/api/entities/ops/${shortId}/verification/`,
      payload,
    );
  }

  /**
   * Record the CIPC registration date with the CIPC document it was read from
   * (FLOW-356). Captured once — a second capture is a 409; send
   * `correction_reason` to correct it, which keeps the captured date on record.
   */
  opsRecordCipcRegistrationDate(
    shortId: string,
    payload: CipcRegistrationDatePayload,
  ): Observable<EntityVerificationState> {
    const form = new FormData();
    form.append('registration_date', payload.registration_date);
    form.append('file', payload.file, payload.file.name);
    if (payload.correction_reason !== undefined) {
      form.append('correction_reason', payload.correction_reason);
    }
    return this.http.post<EntityVerificationState>(
      `${environment.apiUrl}/api/entities/ops/${shortId}/cipc-registration-date/`,
      form,
    );
  }

  /** The CIPC document a registration-date record was read from. */
  opsCipcRegistrationFile(shortId: string, fileId: number): Observable<Blob> {
    return this.http.get(
      `${environment.apiUrl}/api/entities/ops/${shortId}/cipc-registration-date/files/${fileId}/`,
      { responseType: 'blob' },
    );
  }

  // -------------------------------------------------------------------------
  // `bank_accounts` (BRE-SCH-001 3.23) — Ops-only (FLOW-171)
  // -------------------------------------------------------------------------

  /** Every account associated with this business. Ops-only (N4). */
  opsListBankAccounts(shortId: string): Observable<{ bank_accounts: BankAccount[] }> {
    return this.http.get<{ bank_accounts: BankAccount[] }>(
      `${environment.apiUrl}/api/entities/ops/${shortId}/bank-accounts/`,
    );
  }

  /**
   * Capture an account the client has declared.
   *
   * ⚠️ **Capture is not verification.** The new account comes back with
   * `ownership_verified: false`; confirming it belongs to the business is a
   * separate act through `opsRecordVerification`, naming this account.
   * Collapsing the two would make declaring an account verify it.
   */
  opsCreateBankAccount(
    shortId: string,
    payload: CreateBankAccountPayload,
  ): Observable<BankAccount> {
    return this.http.post<BankAccount>(
      `${environment.apiUrl}/api/entities/ops/${shortId}/bank-accounts/`,
      payload,
    );
  }
}
